import { useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { toast } from 'react-toastify';
import useAxios, { BASE_URL } from '../api/axios';
import useAuth from '../hooks/useAuth';
import { Button } from '../components/Button';

const emptyLink = { url: '', membershipLevel: '', ageCondition: '', price: '' };
const organizerRoleOptions = [
  { value: 'management', label: 'hallinta' },
  { value: 'user_management', label: 'käyttäjähallinta' },
  { value: 'competition_organizer', label: 'kilpailun järjestäjä' },
];

const createEmptyForm = () => ({
  organizerID: null,
  name: '',
  bankAccount: '',
  bankRecipientName: '',
  enableBankPayment: false,
  mobilepayNumber: '',
  enableMobilepayPayment: false,
  bankPaymentMessage: '',
  bankPaymentBarcode: '',
  usePersonalReferenceNumber: false,
  mobilepayMessage: '',
  isSflMemberClub: false,
  membershipLinks: [{ ...emptyLink }],
  logoPreviewUrl: '',
  logoScale: 1,
  logoOffsetX: 0,
  logoOffsetY: 0,
  logoDataUrl: '',
  logoFileName: '',
});

const inputSx = { backgroundColor: 'white', borderRadius: '12px' };

const resolveAssetUrl = (assetUrl) => {
  if (!assetUrl) {
    return '';
  }

  if (/^https?:\/\//i.test(assetUrl)) {
    return assetUrl;
  }

  return `${BASE_URL}${assetUrl.startsWith('/') ? assetUrl : `/${assetUrl}`}`;
};

const getLogoImageStyle = (scale = 1, offsetX = 0, offsetY = 0) => ({
  transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
  transformOrigin: 'center center',
});

const buildFinnishReferenceNumber = (baseNumber) => {
  const digits = String(baseNumber || '').replace(/\D+/g, '').replace(/^0+/, '');

  if (!digits) {
    return '';
  }

  const normalizedDigits = digits.length < 3 ? digits.padEnd(3, '1') : digits.slice(0, 5);
  const weights = [7, 3, 1];
  let sum = 0;
  let weightIndex = 0;

  for (let index = normalizedDigits.length - 1; index >= 0; index -= 1) {
    sum += Number(normalizedDigits[index]) * weights[weightIndex % 3];
    weightIndex += 1;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return `${normalizedDigits}${checkDigit}`;
};

const isValidFinnishReferenceNumber = (referenceNumber) => {
  const digits = String(referenceNumber || '').replace(/\D+/g, '');

  if (digits.length < 4 || digits.length > 20 || digits.startsWith('0')) {
    return false;
  }

  const base = digits.slice(0, -1);
  const checkDigit = Number(digits.slice(-1));
  const weights = [7, 3, 1];
  let sum = 0;

  for (let index = base.length - 1, weightIndex = 0; index >= 0; index -= 1, weightIndex += 1) {
    sum += Number(base[index]) * weights[weightIndex % 3];
  }

  return ((10 - (sum % 10)) % 10) === checkDigit;
};

const resolveBankPaymentMode = ({ usePersonalReferenceNumber, bankPaymentBarcode, bankPaymentMessage }) => {
  if (usePersonalReferenceNumber) {
    return 'personalReference';
  }

  if (String(bankPaymentBarcode || '').trim()) {
    return 'reference';
  }

  if (String(bankPaymentMessage || '').trim()) {
    return 'message';
  }

  return 'message';
};

function Organizers() {
  const api = useAxios();
  const { auth, isAuthenticated, isAdmin } = useAuth();
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(createEmptyForm());
  const [organizerBankPaymentMode, setOrganizerBankPaymentMode] = useState('message');
  const [editingOrganizerId, setEditingOrganizerId] = useState(null);
  const [expandedOrganizerID, setExpandedOrganizerID] = useState(null);
  const [membershipConfirmState, setMembershipConfirmState] = useState(null);
  const [removeOrganizerUserState, setRemoveOrganizerUserState] = useState(null);
  const [removingOrganizerUser, setRemovingOrganizerUser] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedOrganizerForUsers, setSelectedOrganizerForUsers] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [userModalLoading, setUserModalLoading] = useState(false);
  const [userSaving, setUserSaving] = useState(false);
  const [userForm, setUserForm] = useState({ userID: '', email: '', roles: [] });
  const [editingOrganizerUser, setEditingOrganizerUser] = useState(null);

  const showCreateOrganizerAction = isAdmin();
  const viewerBirthYear = Number(auth?.user?.year ?? 0);
  const viewerAge = Number.isFinite(viewerBirthYear) && viewerBirthYear > 1900
    ? new Date().getFullYear() - viewerBirthYear
    : null;

  const updateOrganizerInState = (updatedOrganizer) => {
    if (!updatedOrganizer) {
      return;
    }

    setOrganizers((prev) => prev
      .map((item) => (item.organizerID === updatedOrganizer.organizerID ? updatedOrganizer : item))
      .sort((left, right) => left.name.localeCompare(right.name, 'fi')));
  };

  const organizerCanManage = (organizer) => Boolean(organizer?.permissions?.canManageOrganizer) || isAdmin();
  const organizerCanManageUsers = (organizer) => Boolean(organizer?.permissions?.canManageUsers) || isAdmin();
  const organizerIsMember = (organizer) => Boolean(organizer?.permissions?.isOrganizerUser) || isAdmin();
  const hasInvalidSharedReference = organizerBankPaymentMode === 'reference'
    && form.bankPaymentBarcode.trim() !== ''
    && !isValidFinnishReferenceNumber(form.bankPaymentBarcode);

  const parseAgeCondition = (condition) => {
    const normalizedCondition = String(condition || '').trim().toLowerCase();

    if (!normalizedCondition) {
      return { type: 'none' };
    }

    const rangeMatch = normalizedCondition.match(/(\d{1,2})\s*[-–]\s*(\d{1,2})/);
    if (rangeMatch) {
      return { type: 'range', min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
    }

    const lessOrEqualMatch = normalizedCondition.match(/^(<=\s*|alle\s+|intaan\s+)(\d{1,2})$/);
    if (lessOrEqualMatch) {
      return { type: 'maxInclusive', value: Number(lessOrEqualMatch[2]) };
    }

    const lessMatch = normalizedCondition.match(/^(<|alle|under)\s*(\d{1,2})$/);
    if (lessMatch) {
      return { type: 'maxExclusive', value: Number(lessMatch[2]) };
    }

    const greaterOrEqualMatch = normalizedCondition.match(/^(>=)\s*(\d{1,2})$|^(\d{1,2})\s*\+$/);
    if (greaterOrEqualMatch) {
      return { type: 'minInclusive', value: Number(greaterOrEqualMatch[2] || greaterOrEqualMatch[3]) };
    }

    const greaterMatch = normalizedCondition.match(/^(>|yli|over)\s*(\d{1,2})$/);
    if (greaterMatch) {
      return { type: 'minExclusive', value: Number(greaterMatch[2]) };
    }

    return { type: 'unknown' };
  };

  const matchesAgeCondition = (condition, age) => {
    if (!condition) {
      return { isSpecific: false, matches: true };
    }

    if (age === null || age === undefined) {
      return { isSpecific: true, matches: null };
    }

    const parsed = parseAgeCondition(condition);

    switch (parsed.type) {
      case 'none':
        return { isSpecific: false, matches: true };
      case 'range':
        return { isSpecific: true, matches: age >= parsed.min && age <= parsed.max };
      case 'maxInclusive':
        return { isSpecific: true, matches: age <= parsed.value };
      case 'maxExclusive':
        return { isSpecific: true, matches: age < parsed.value };
      case 'minInclusive':
        return { isSpecific: true, matches: age >= parsed.value };
      case 'minExclusive':
        return { isSpecific: true, matches: age > parsed.value };
      default:
        return { isSpecific: true, matches: null };
    }
  };

  const getVisibleMembershipLinks = (organizer) => {
    const links = Array.isArray(organizer?.membershipLinks) ? organizer.membershipLinks : [];

    if (organizerIsMember(organizer) || viewerAge === null) {
      return links;
    }

    const evaluatedLinks = links.map((link) => ({
      link,
      evaluation: matchesAgeCondition(link?.ageCondition, viewerAge),
    }));

    const specificMatches = evaluatedLinks.filter(({ evaluation }) => evaluation.isSpecific && evaluation.matches === true).map(({ link }) => link);

    if (specificMatches.length > 0) {
      return specificMatches;
    }

    return evaluatedLinks
      .filter(({ evaluation }) => !evaluation.isSpecific || evaluation.matches === null)
      .map(({ link }) => link);
  };

  const toggleOrganizer = (organizerID) => {
    setExpandedOrganizerID((previousValue) => (previousValue === organizerID ? null : organizerID));
  };

  const openMembershipConfirm = (organizer, link) => {
    if (!link?.url) {
      return;
    }

    setMembershipConfirmState({
      url: link.url,
      organizerName: organizer?.name || 'Seura',
      membershipLevel: link?.membershipLevel || 'Jäsenyys',
    });
  };

  const closeMembershipConfirm = () => {
    setMembershipConfirmState(null);
  };

  const closeRemoveOrganizerUserModal = () => {
    if (removingOrganizerUser) {
      return;
    }

    setRemoveOrganizerUserState(null);
  };

  const confirmMembershipLink = () => {
    if (!membershipConfirmState?.url) {
      return;
    }

    window.open(membershipConfirmState.url, '_blank', 'noopener,noreferrer');
    closeMembershipConfirm();
  };

  useEffect(() => {
    let isMounted = true;

    const loadOrganizers = async () => {
      try {
        setLoading(true);
        const response = await api.post(
          '/getOrganizers.php',
          JSON.stringify({}),
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (!isMounted) {
          return;
        }

        setOrganizers(Array.isArray(response?.data?.organizers) ? response.data.organizers : []);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        const message = err?.response?.data?.message || err?.response?.data?.msg;
        toast.error(message || 'Järjestäjien haku epäonnistui');
        setOrganizers([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadOrganizers();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const organizerCountLabel = useMemo(() => {
    if (organizers.length === 1) {
      return '1 järjestäjä';
    }

    return `${organizers.length} järjestäjää`;
  }, [organizers.length]);

  const resetForm = () => {
    setForm(createEmptyForm());
    setSaving(false);
    setEditingOrganizerId(null);
  };

  const resetUserForm = () => {
    setUserForm({ userID: '', email: '', roles: [] });
    setAssignableUsers([]);
    setUserSaving(false);
    setUserModalLoading(false);
    setSelectedOrganizerForUsers(null);
    setEditingOrganizerUser(null);
  };

  const openModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openUserModal = async (organizer) => {
    setSelectedOrganizerForUsers(organizer);
    setUserForm({ userID: '', email: '', roles: [] });
    setAssignableUsers([]);
    setEditingOrganizerUser(null);
    setIsUserModalOpen(true);
    setUserModalLoading(isAdmin());

    if (!isAdmin()) {
      return;
    }

    try {
      const response = await api.post(
        '/getOrganizerAssignableUsers.php',
        JSON.stringify({ organizerID: organizer.organizerID }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      setAssignableUsers(Array.isArray(response?.data?.users) ? response.data.users : []);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Käyttäjien haku epäonnistui');
      setIsUserModalOpen(false);
      resetUserForm();
    } finally {
      setUserModalLoading(false);
    }
  };

  const openEditUserModal = (organizer, organizerUser) => {
    setSelectedOrganizerForUsers(organizer);
    setEditingOrganizerUser(organizerUser);
    setUserForm({
      userID: organizerUser?.userID ? String(organizerUser.userID) : '',
      email: organizerUser?.email || '',
      roles: Array.isArray(organizerUser?.roles) ? organizerUser.roles : [],
    });
    setAssignableUsers([]);
    setUserModalLoading(false);
    setIsUserModalOpen(true);
  };

  const openEditModal = (organizer) => {
    const membershipLinks = Array.isArray(organizer?.membershipLinks) && organizer.membershipLinks.length > 0
      ? organizer.membershipLinks.map((link) => ({
        url: link?.url || '',
        membershipLevel: link?.membershipLevel || '',
        ageCondition: link?.ageCondition || '',
        price: link?.price || '',
      }))
      : [{ ...emptyLink }];

    setForm({
      organizerID: organizer?.organizerID ?? null,
      name: organizer?.name || '',
      bankAccount: organizer?.bankAccount || '',
      bankRecipientName: organizer?.bankRecipientName || organizer?.name || '',
      enableBankPayment: Boolean(organizer?.enableBankPayment),
      mobilepayNumber: organizer?.mobilepayNumber || '',
      enableMobilepayPayment: Boolean(organizer?.enableMobilepayPayment),
      bankPaymentMessage: organizer?.bankPaymentMessage || '',
      bankPaymentBarcode: organizer?.bankPaymentBarcode || '',
      usePersonalReferenceNumber: Boolean(organizer?.usePersonalReferenceNumber),
      mobilepayMessage: organizer?.mobilepayMessage || '',
      isSflMemberClub: Boolean(organizer?.isSflMemberClub),
      membershipLinks,
      logoPreviewUrl: resolveAssetUrl(organizer?.logoUrl || ''),
      logoScale: Number(organizer?.logoScale ?? 1),
      logoOffsetX: Number(organizer?.logoOffsetX ?? 0),
      logoOffsetY: Number(organizer?.logoOffsetY ?? 0),
      logoDataUrl: '',
      logoFileName: '',
    });
    setOrganizerBankPaymentMode(resolveBankPaymentMode(organizer || {}));
    setEditingOrganizerId(organizer?.organizerID ?? null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }

    setIsModalOpen(false);
    setOrganizerBankPaymentMode('message');
    resetForm();
  };

  const closeUserModal = () => {
    if (userSaving) {
      return;
    }

    setIsUserModalOpen(false);
    resetUserForm();
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBankPaymentModeChange = (nextMode) => {
    setOrganizerBankPaymentMode(nextMode);
    setForm((prev) => ({
      ...prev,
      usePersonalReferenceNumber: nextMode === 'personalReference',
      bankPaymentMessage: nextMode === 'message' ? prev.bankPaymentMessage : '',
      bankPaymentBarcode: nextMode === 'reference' ? prev.bankPaymentBarcode : '',
      mobilepayMessage: nextMode === 'personalReference' ? '' : prev.mobilepayMessage,
    }));
  };

  const handleGenerateReferenceNumber = () => {
    const currentDigits = String(form.bankPaymentBarcode || '').replace(/\D+/g, '').replace(/^0+/, '');
    const nextReference = currentDigits && !isValidFinnishReferenceNumber(currentDigits)
      ? buildFinnishReferenceNumber(currentDigits)
      : buildFinnishReferenceNumber(`${Date.now()}`.slice(-5));

    setForm((prev) => ({
      ...prev,
      bankPaymentBarcode: nextReference,
    }));
  };

  const handleLogoLayoutChange = (field) => (event) => {
    const value = Number(event.target.value);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUserFieldChange = (field) => (event) => {
    const value = event.target.value;
    setUserForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role) => (event) => {
    const checked = event.target.checked;
    setUserForm((prev) => ({
      ...prev,
      roles: checked
        ? [...prev.roles, role].filter((value, index, array) => array.indexOf(value) === index)
        : prev.roles.filter((value) => value !== role),
    }));
  };

  const handleMemberClubToggle = (event) => {
    const checked = event.target.checked;
    setForm((prev) => ({
      ...prev,
      isSflMemberClub: checked,
      membershipLinks: checked ? prev.membershipLinks : [{ ...emptyLink }],
    }));
  };

  const handleMembershipLinkChange = (index, field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      membershipLinks: prev.membershipLinks.map((link, linkIndex) => (
        linkIndex === index ? { ...link, [field]: value } : link
      )),
    }));
  };

  const addMembershipLinkRow = () => {
    setForm((prev) => ({
      ...prev,
      membershipLinks: [...prev.membershipLinks, { ...emptyLink }],
    }));
  };

  const removeMembershipLinkRow = (index) => {
    setForm((prev) => ({
      ...prev,
      membershipLinks: prev.membershipLinks.filter((_, linkIndex) => linkIndex !== index),
    }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      setForm((prev) => ({ ...prev, logoDataUrl: '', logoFileName: '' }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Valitse logoksi kuvatiedosto.');
      event.target.value = '';
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo saa olla enintään 3 Mt.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        logoPreviewUrl: typeof reader.result === 'string' ? reader.result : prev.logoPreviewUrl,
        logoDataUrl: typeof reader.result === 'string' ? reader.result : '',
        logoFileName: file.name,
      }));
    };
    reader.onerror = () => {
      toast.error('Logon lukeminen epäonnistui.');
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.error('Anna yhdistyksen / yrityksen nimi');
      return false;
    }

    if (form.isSflMemberClub) {
      const hasInvalidRow = form.membershipLinks.some((link) => {
        const hasAnyValue = link.url.trim() || link.membershipLevel.trim() || link.ageCondition.trim() || link.price.trim();

        if (!hasAnyValue) {
          return false;
        }

        return !link.url.trim() || !/^https?:\/\//i.test(link.url.trim());
      });

      if (hasInvalidRow) {
        toast.error('Tarkista jäsenlinkkien osoitteet.');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      organizerID: editingOrganizerId,
      name: form.name.trim(),
      bankAccount: form.bankAccount.trim(),
      bankRecipientName: (form.bankRecipientName || form.name).trim(),
      enableBankPayment: form.enableBankPayment,
      mobilepayNumber: form.mobilepayNumber.trim(),
      enableMobilepayPayment: form.enableMobilepayPayment,
      bankPaymentMessage: form.usePersonalReferenceNumber ? '' : form.bankPaymentMessage.trim(),
      bankPaymentBarcode: form.usePersonalReferenceNumber ? '' : form.bankPaymentBarcode.trim(),
      usePersonalReferenceNumber: form.usePersonalReferenceNumber,
      mobilepayMessage: form.usePersonalReferenceNumber ? '' : form.mobilepayMessage.trim(),
      isSflMemberClub: form.isSflMemberClub,
      membershipLinks: form.isSflMemberClub
        ? form.membershipLinks.filter((link) => link.url.trim() || link.membershipLevel.trim() || link.ageCondition.trim() || link.price.trim())
        : [],
      logoScale: form.logoScale,
      logoOffsetX: form.logoOffsetX,
      logoOffsetY: form.logoOffsetY,
      logoDataUrl: form.logoDataUrl,
      logoFileName: form.logoFileName,
    };

    try {
      setSaving(true);
      const endpoint = editingOrganizerId ? '/updateOrganizer.php' : '/addOrganizer.php';
      const response = await api.post(
        endpoint,
        JSON.stringify({ organizer: payload }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      const organizer = response?.data?.organizer;
      if (organizer) {
        setOrganizers((prev) => {
          const nextOrganizers = editingOrganizerId
            ? prev.map((item) => (item.organizerID === organizer.organizerID ? organizer : item))
            : [...prev, organizer];

          return nextOrganizers.sort((left, right) => left.name.localeCompare(right.name, 'fi'));
        });
      }

      toast.success(editingOrganizerId ? 'Järjestäjä päivitetty.' : 'Järjestäjä lisätty.');
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || (editingOrganizerId ? 'Järjestäjän päivittäminen epäonnistui' : 'Järjestäjän lisääminen epäonnistui'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddOrganizerUser = async (event) => {
    event.preventDefault();

    if (!selectedOrganizerForUsers?.organizerID) {
      return;
    }

    const trimmedEmail = userForm.email.trim().toLowerCase();

    if (!editingOrganizerUser && isAdmin()) {
      if (!userForm.userID && !trimmedEmail) {
        toast.error('Valitse käyttäjä tai anna sähköpostiosoite');
        return;
      }

      if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        toast.error('Sähköpostiosoite on virheellinen');
        return;
      }
    } else if (!editingOrganizerUser) {
      if (!trimmedEmail) {
        toast.error('Anna sähköpostiosoite');
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        toast.error('Sähköpostiosoite on virheellinen');
        return;
      }
    }

    if (userForm.roles.length === 0) {
      toast.error('Valitse vähintään yksi rooli');
      return;
    }

    try {
      setUserSaving(true);
      const organizerUserPayload = editingOrganizerUser
        ? {
          organizerID: selectedOrganizerForUsers.organizerID,
          organizerUserID: editingOrganizerUser.organizerUserID,
          roles: userForm.roles,
        }
        : {
          organizerID: selectedOrganizerForUsers.organizerID,
          roles: userForm.roles,
          ...(trimmedEmail ? { email: trimmedEmail } : { userID: Number(userForm.userID) }),
        };
      const response = await api.post(
        editingOrganizerUser ? '/updateOrganizerUser.php' : '/addOrganizerUser.php',
        JSON.stringify({ organizerUser: organizerUserPayload }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response?.data?.organizer) {
        updateOrganizerInState(response.data.organizer);
      }

      toast.success(
        editingOrganizerUser
          ? 'Käyttäjän roolit päivitetty.'
          : response?.data?.invitationSent
            ? 'Kutsu lähetetty.'
            : 'Käyttäjä lisätty järjestäjälle.'
      );
      setIsUserModalOpen(false);
      resetUserForm();
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || (editingOrganizerUser ? 'Käyttäjän muokkaaminen epäonnistui' : 'Käyttäjän lisääminen epäonnistui'));
    } finally {
      setUserSaving(false);
    }
  };

  const handleRemoveOrganizerUser = (organizer, organizerUser) => {
    setRemoveOrganizerUserState({ organizer, organizerUser });
  };

  const confirmRemoveOrganizerUser = async () => {
    const organizer = removeOrganizerUserState?.organizer;
    const organizerUser = removeOrganizerUserState?.organizerUser;

    if (!organizer?.organizerID || !organizerUser?.organizerUserID) {
      return;
    }

    try {
      setRemovingOrganizerUser(true);
      const response = await api.post(
        '/removeOrganizerUser.php',
        JSON.stringify({ organizerUser: { organizerID: organizer.organizerID, organizerUserID: organizerUser.organizerUserID, userID: organizerUser.userID } }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response?.data?.organizer) {
        updateOrganizerInState(response.data.organizer);
      }

      toast.success('Käyttäjä poistettu järjestäjältä.');
      setRemoveOrganizerUserState(null);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Käyttäjän poistaminen epäonnistui');
    } finally {
      setRemovingOrganizerUser(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-12 pt-24">
      <div className="mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-6 shadow-2xl shadow-slate-950/30 backdrop-blur md:flex md:items-start md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Disc Golf Reikäpeli Cup</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">Järjestäjät</h1>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
            Tutustu järjestäjiin ja avaa kortista tarkemmat tiedot. Järjestäjän omat käyttäjät näkevät lisäksi hallintatiedot.
          </p>
        </div>

        <div className="mt-4 flex items-center gap-3 md:mt-0">
          <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm text-slate-100">
            {organizerCountLabel}
          </div>
          {showCreateOrganizerAction ? <Button name="Lisää uusi" type="PRIMARY" action={openModal} /> : null}
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300">
          <p className="text-lg font-semibold text-white">Ladataan järjestäjiä...</p>
        </div>
      ) : organizers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300">
          <p className="text-lg font-semibold text-white">Järjestäjiä ei ole vielä lisätty.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {organizers.map((organizer) => {
            const isExpanded = expandedOrganizerID === organizer.organizerID;
            const ChevronIcon = isExpanded ? FiChevronUp : FiChevronDown;
            const canViewInternals = organizerIsMember(organizer);
            const visibleMembershipLinks = getVisibleMembershipLinks(organizer);

            return (
              <article key={organizer.organizerID} className="rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur transition hover:border-cyan-300/30">
                <button
                  type="button"
                  onClick={() => toggleOrganizer(organizer.organizerID)}
                  className="flex w-full items-center gap-4 text-left"
                >
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                    {organizer.logoUrl ? (
                      <img src={resolveAssetUrl(organizer.logoUrl)} alt={organizer.name} className="h-full w-full object-cover" style={getLogoImageStyle(organizer.logoScale, organizer.logoOffsetX, organizer.logoOffsetY)} />
                    ) : (
                      <span className="px-3 text-center text-xs uppercase tracking-[0.25em] text-slate-400">Ei logoa</span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Järjestäjä</p>
                    <h2 className="mt-2 text-xl font-bold text-white">{organizer.name}</h2>
                  </div>

                  <span
                    aria-label={isExpanded ? 'Sulje järjestäjän tiedot' : 'Avaa järjestäjän tiedot'}
                    title={isExpanded ? 'Sulje järjestäjän tiedot' : 'Avaa järjestäjän tiedot'}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 transition hover:bg-white/10"
                  >
                    <ChevronIcon className="text-lg" />
                  </span>
                </button>

                {isExpanded ? (
                  <div className="mt-4 space-y-4" onClick={(event) => event.stopPropagation()}>
                    {canViewInternals ? (
                      <>
                        <div className="grid gap-4 lg:grid-cols-2">
                          <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 text-sm text-slate-300">
                            <div className="border-b border-white/10 pb-3">
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Tilimaksu</p>
                              <p className="mt-1 text-slate-400">Järjestäjän pankkitiliin liittyvät maksutiedot.</p>
                            </div>
                            <div className="mt-3 grid gap-2">
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">Maksu tilille</span>
                                <span className="text-white">{organizer.enableBankPayment ? 'Käytössä' : 'Ei käytössä'}</span>
                              </div>
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">Tilinumero</span>
                                <span className="text-white">{organizer.bankAccount || 'Ei asetettu'}</span>
                              </div>
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">Tilisiirron saaja</span>
                                <span className="text-white">{organizer.bankRecipientName || organizer.name || 'Ei asetettu'}</span>
                              </div>
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">Tilisiirron viesti</span>
                                <span className="text-white">{organizer.bankPaymentMessage || 'Ei asetettu'}</span>
                              </div>
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">Yhteinen viitenumero</span>
                                <span className="text-white">{organizer.bankPaymentBarcode || 'Ei asetettu'}</span>
                              </div>
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">Henkilökohtainen viite</span>
                                <span className="text-white">{organizer.usePersonalReferenceNumber ? 'Käytössä' : 'Ei käytössä'}</span>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 text-sm text-slate-300">
                            <div className="border-b border-white/10 pb-3">
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">MobilePay</p>
                              <p className="mt-1 text-slate-400">Järjestäjän MobilePay-maksuihin liittyvät tiedot.</p>
                            </div>
                            <div className="mt-3 grid gap-2">
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">MobilePay maksu</span>
                                <span className="text-white">{organizer.enableMobilepayPayment ? 'Käytössä' : 'Ei käytössä'}</span>
                              </div>
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">MobilePay</span>
                                <span className="text-white">{organizer.mobilepayNumber || 'Ei asetettu'}</span>
                              </div>
                              <div className="rounded-2xl bg-white/5 p-3">
                                <span className="block text-xs uppercase tracking-[0.2em] text-cyan-300">MobilePay-viesti</span>
                                <span className="text-white">{organizer.mobilepayMessage || 'Ei asetettu'}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 text-sm text-slate-300">
                          <p className="font-semibold text-white">Suomen Frisbeegolfliiton jäsenseura</p>
                          <p className="mt-2">{organizer.isSflMemberClub ? 'Kyllä' : 'Ei'}</p>

                          {organizer.isSflMemberClub && visibleMembershipLinks.length > 0 ? (
                            <div className="mt-4 space-y-2">
                              {visibleMembershipLinks.map((link, index) => (
                                <div key={`${organizer.organizerID}-${index}`} className="rounded-2xl bg-white/5 p-3">
                                  <p className="font-semibold text-white">{link.membershipLevel || 'Jäsentaso'}</p>
                                  {link.ageCondition ? <p className="mt-1 text-slate-300">Ikäehto: {link.ageCondition}</p> : null}
                                  <p className="mt-1 text-slate-300">Hinta: {link.price || 'Ei asetettu'}</p>
                                  <a href={link.url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-cyan-300 underline hover:text-cyan-200">
                                    {link.url}
                                  </a>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 text-sm text-slate-300">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-white">Järjestäjän käyttäjät</p>
                              <p className="mt-1 text-xs text-slate-400">Käyttöoikeudet järjestäjäkohtaisiin toimintoihin.</p>
                            </div>
                            {organizerCanManageUsers(organizer) ? <Button name="Lisää" type="PRIMARY" action={() => openUserModal(organizer)} /> : null}
                          </div>

                          {Array.isArray(organizer.organizerUsers) && organizer.organizerUsers.length > 0 ? (
                            <div className="mt-4 space-y-3">
                              {organizer.organizerUsers.map((organizerUser) => (
                                <div key={`${organizer.organizerID}-${organizerUser.organizerUserID || organizerUser.userID || organizerUser.email}`} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                      <p className="font-semibold text-white">{organizerUser.name}</p>
                                      <p className="mt-1 text-xs text-slate-400">{organizerUser.email || 'Ei sähköpostia'}</p>
                                      {organizerUser.isPending ? <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Kutsu odottaa rekisteröitymistä</p> : null}
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {(organizerUser.roleLabels || []).map((roleLabel) => (
                                          <span key={`${organizerUser.organizerUserID || organizerUser.userID || organizerUser.email}-${roleLabel}`} className="rounded-full border border-cyan-300/30 bg-cyan-400/10 px-2.5 py-1 text-[11px] font-semibold text-cyan-100">
                                            {roleLabel}
                                          </span>
                                        ))}
                                        {organizerUser.isAdmin ? <span className="rounded-full border border-amber-300/30 bg-amber-300/10 px-2.5 py-1 text-[11px] font-semibold text-amber-100">Sivuston admin</span> : null}
                                      </div>
                                    </div>

                                    {organizerCanManageUsers(organizer) ? (
                                      <div className="flex flex-wrap justify-end gap-2">
                                        <Button name="Muokkaa" type="EDIT" action={() => openEditUserModal(organizer, organizerUser)} />
                                        <Button name="Poista" type="DELETE" action={() => handleRemoveOrganizerUser(organizer, organizerUser)} />
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-4 text-sm text-slate-400">Järjestäjälle ei ole vielä lisätty käyttäjiä.</p>
                          )}
                        </div>

                        {organizerCanManage(organizer) ? (
                          <div className="flex justify-end">
                            <Button name="Muokkaa" type="EDIT" action={() => openEditModal(organizer)} />
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="rounded-2xl border border-white/10 bg-slate-950/35 p-4 text-sm text-slate-300">
                        {organizer.isSflMemberClub && visibleMembershipLinks.length > 0 ? (
                          <div className="space-y-2">
                            {visibleMembershipLinks.map((link, index) => (
                              <div key={`${organizer.organizerID}-public-${index}`} className="rounded-2xl bg-white/5 p-3">
                                <p className="font-semibold text-white">{link.membershipLevel || 'Jäsentaso'}</p>
                                {link.price ? <p className="mt-1 text-slate-300">Hinta: {link.price}</p> : null}
                                  <button
                                    type="button"
                                    onClick={() => openMembershipConfirm(organizer, link)}
                                    className="mt-2 inline-block text-cyan-300 underline hover:text-cyan-200"
                                  >
                                    Liity seuraan
                                  </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-400">Julkisia seuraan liittymislinkkejä ei ole saatavilla.</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={closeModal}>
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Hallinta</p>
                <h2 className="mt-2 text-2xl font-black text-white">{editingOrganizerId ? 'Muokkaa järjestäjää' : 'Lisää järjestäjä'}</h2>
              </div>
              <button type="button" onClick={closeModal} className="rounded-full border border-white/10 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10">
                Sulje
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <TextField
                  variant="filled"
                  label="Yhdistyksen / yrityksen nimi"
                  value={form.name}
                  onChange={handleFieldChange('name')}
                  required
                  sx={inputSx}
                  fullWidth
                />
              </div>

              <div className="rounded-[28px] border border-white/10 bg-white/5 p-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Maksutiedot</p>
                  <span
                    title="Järjestäjän maksutiedot listään automaattisesti järjestäjän lisäämiin kilpailu tietoihin. Kilpailun maksutietoja pystyy muokkaamaan."
                    aria-label="Järjestäjän maksutiedot lisätään automaattisesti järjestäjän lisäämiin kilpailun tietoihin. Kilpailun maksutietoja pystyy muokkaamaan."
                    className="inline-flex h-6 w-6 cursor-help items-center justify-center rounded-full border border-cyan-300/40 bg-cyan-400/10 text-xs font-black text-cyan-200"
                  >
                    ?
                  </span>
                </div>

                <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/20 p-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Tilille maksu</p>
                  <div className="mt-3">
                    <FormControlLabel
                      control={<Checkbox checked={form.enableBankPayment} onChange={(event) => setForm((prev) => ({ ...prev, enableBankPayment: event.target.checked }))} sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }} />}
                      label={<span className="text-sm font-semibold text-white">Maksu tilille</span>}
                    />
                  </div>

                  {form.enableBankPayment ? (
                    <>
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <TextField
                          variant="filled"
                          label="Tilinumero"
                          value={form.bankAccount}
                          onChange={handleFieldChange('bankAccount')}
                          sx={inputSx}
                          fullWidth
                        />
                        <div className="flex items-center gap-2">
                          <TextField
                            variant="filled"
                            label="Tilisiirron saaja"
                            value={form.bankRecipientName || form.name}
                            onChange={handleFieldChange('bankRecipientName')}
                            sx={inputSx}
                            helperText="Oletuksena järjestäjän nimi, mutta kenttää voi muokata."
                            fullWidth
                          />
                          {(form.bankRecipientName || form.name) && (
                            <button
                              type="button"
                              title="Kopioi saajan nimi"
                              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-slate-700 hover:bg-slate-100"
                              onClick={() => navigator.clipboard?.writeText(form.bankRecipientName || form.name)}
                            >
                              Kopioi
                            </button>
                          )}
                        </div>
                      </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <button
                      type="button"
                      onClick={() => handleBankPaymentModeChange('message')}
                      className={`rounded-2xl border px-4 py-4 text-left transition ${
                        organizerBankPaymentMode === 'message'
                          ? 'border-cyan-300 bg-cyan-400/15 text-white'
                          : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
                      }`}
                    >
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Viesti</p>
                      <p className="mt-2 text-sm leading-6">Näytä tilisiirrossa maksuviesti.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBankPaymentModeChange('reference')}
                      className={`rounded-2xl border px-4 py-4 text-left transition ${
                        organizerBankPaymentMode === 'reference'
                          ? 'border-cyan-300 bg-cyan-400/15 text-white'
                          : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
                      }`}
                    >
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Viitenumero</p>
                      <p className="mt-2 text-sm leading-6">Käytä samaa suomalaista viitenumeroa kaikilla maksuilla.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBankPaymentModeChange('personalReference')}
                      className={`rounded-2xl border px-4 py-4 text-left transition ${
                        organizerBankPaymentMode === 'personalReference'
                          ? 'border-cyan-300 bg-cyan-400/15 text-white'
                          : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10'
                      }`}
                    >
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Henkilökohtainen viite</p>
                      <p className="mt-2 text-sm leading-6">Luo pelaajille suomalainen viitenumero myös MobilePayhin.</p>
                    </button>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {organizerBankPaymentMode === 'message' ? (
                      <TextField
                        variant="filled"
                        label="Tilisiirron viesti"
                        value={form.bankPaymentMessage}
                        onChange={handleFieldChange('bankPaymentMessage')}
                        sx={inputSx}
                        fullWidth
                        className="md:col-span-2"
                      />
                    ) : null}

                    {organizerBankPaymentMode === 'reference' ? (
                      <div className="md:col-span-2 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                        <TextField
                          variant="filled"
                          label="Yhteinen viitenumero"
                          value={form.bankPaymentBarcode}
                          onChange={(event) => setForm((prev) => ({ ...prev, bankPaymentBarcode: event.target.value.replace(/\D+/g, '').replace(/^0+/, '') }))}
                          sx={inputSx}
                          error={hasInvalidSharedReference}
                          helperText={hasInvalidSharedReference ? 'Viitenumero ei ole validi suomalainen viitenumero. Käytä Generoi laskeaksesi tarkistemerkin.' : 'Anna validi suomalainen viitenumero tai generoi uusi.'}
                          fullWidth
                        />
                        <button
                          type="button"
                          onClick={handleGenerateReferenceNumber}
                          className="inline-flex h-[56px] items-center justify-center rounded-2xl border border-cyan-300 bg-cyan-400/15 px-4 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/25"
                        >
                          Generoi
                        </button>
                      </div>
                    ) : null}

                    {organizerBankPaymentMode === 'personalReference' ? (
                      <div className="md:col-span-2 rounded-2xl border border-cyan-300/30 bg-cyan-400/10 px-4 py-4 text-sm leading-6 text-slate-200">
                        Järjestäjän kilpailuihin voidaan kopioida asetus, jossa pelaajalle luodaan rekisteröityessä henkilökohtainen suomalainen viitenumero. Sama viite toimii myös MobilePayssä.
                      </div>
                    ) : null}

                  </div>
                    </>
                  ) : null}
                </div>

                <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/20 p-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">MobilePay</p>
                  <div className="mt-3">
                    <FormControlLabel
                      control={<Checkbox checked={form.enableMobilepayPayment} onChange={(event) => setForm((prev) => ({ ...prev, enableMobilepayPayment: event.target.checked }))} sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }} />}
                      label={<span className="text-sm font-semibold text-white">MobilePay maksu</span>}
                    />
                  </div>

                  {form.enableMobilepayPayment ? (
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <TextField
                        variant="filled"
                        label="MobilePay-numero"
                        value={form.mobilepayNumber}
                        onChange={handleFieldChange('mobilepayNumber')}
                        sx={inputSx}
                        fullWidth
                      />
                      <TextField
                        variant="filled"
                        label={organizerBankPaymentMode === 'personalReference' ? 'MobilePay-viesti poistuu käytöstä' : 'MobilePay-viesti'}
                        value={organizerBankPaymentMode === 'personalReference' ? '' : form.mobilepayMessage}
                        onChange={handleFieldChange('mobilepayMessage')}
                        sx={inputSx}
                        fullWidth
                        disabled={organizerBankPaymentMode === 'personalReference'}
                        helperText={organizerBankPaymentMode === 'personalReference' ? 'Henkilökohtainen viite korvaa MobilePay-viestin.' : 'Kopioidaan kilpailulle oletuksena.'}
                        className="md:col-span-2"
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[28px] border border-white/10 bg-white/5 p-4">
                <FormControlLabel
                  control={<Checkbox checked={form.isSflMemberClub} onChange={handleMemberClubToggle} sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }} />}
                  label={<span className="text-sm font-semibold text-white">Suomen Frisbeegolfliiton jäsenseura</span>}
                />

                {form.isSflMemberClub ? (
                  <div className="mt-4 space-y-3">
                    {form.membershipLinks.map((link, index) => (
                      <div key={`membership-link-${index}`} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                        <div className="grid gap-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.7fr)]">
                          <TextField
                            variant="filled"
                            label="Liittymislinkki"
                            value={link.url}
                            onChange={handleMembershipLinkChange(index, 'url')}
                            sx={inputSx}
                            fullWidth
                          />
                          <TextField
                            variant="filled"
                            label="Jäsentaso"
                            value={link.membershipLevel}
                            onChange={handleMembershipLinkChange(index, 'membershipLevel')}
                            sx={inputSx}
                            fullWidth
                          />
                          <TextField
                            variant="filled"
                            label="Ikäehto"
                            value={link.ageCondition}
                            onChange={handleMembershipLinkChange(index, 'ageCondition')}
                            placeholder="esim. <= 18"
                            sx={inputSx}
                            fullWidth
                          />
                          <TextField
                            variant="filled"
                            label="Hinta"
                            value={link.price}
                            onChange={handleMembershipLinkChange(index, 'price')}
                            sx={inputSx}
                            fullWidth
                          />
                        </div>

                        {form.membershipLinks.length > 1 ? (
                          <div className="mt-3 flex justify-end">
                            <Button name="Poista linkki" type="DELETE" action={() => removeMembershipLinkRow(index)} />
                          </div>
                        ) : null}
                      </div>
                    ))}

                    <Button name="Lisää jäsenlinkki" type="SECONDARY" action={addMembershipLinkRow} />
                  </div>
                ) : null}
              </div>

              <div className="rounded-[28px] border border-white/10 bg-white/5 p-4">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Logo</p>
                <div className="mt-3 grid gap-4 md:grid-cols-[minmax(0,1fr)_180px] md:items-start">
                  <div>
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="block w-full text-sm text-slate-300 file:mr-4 file:rounded-full file:border-0 file:bg-cyan-500 file:px-4 file:py-2 file:font-semibold file:text-slate-950 hover:file:bg-cyan-400" />
                    <p className="mt-2 text-xs text-slate-400">Tuetut muodot: PNG, JPG, WEBP ja SVG. Maksimikoko 3 Mt.</p>

                    {form.logoPreviewUrl ? (
                      <div className="mt-4 space-y-4 rounded-2xl border border-white/10 bg-slate-950/30 p-4">
                        <div>
                          <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-cyan-300">
                            <span>Koko</span>
                            <span>{Number(form.logoScale).toFixed(2)}x</span>
                          </div>
                          <input type="range" min="0.5" max="3" step="0.05" value={form.logoScale} onChange={handleLogoLayoutChange('logoScale')} className="w-full accent-cyan-400" />
                        </div>

                        <div>
                          <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-cyan-300">
                            <span>Siirto vaaka</span>
                            <span>{form.logoOffsetX}px</span>
                          </div>
                          <input type="range" min="-120" max="120" step="1" value={form.logoOffsetX} onChange={handleLogoLayoutChange('logoOffsetX')} className="w-full accent-cyan-400" />
                        </div>

                        <div>
                          <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-cyan-300">
                            <span>Siirto pysty</span>
                            <span>{form.logoOffsetY}px</span>
                          </div>
                          <input type="range" min="-120" max="120" step="1" value={form.logoOffsetY} onChange={handleLogoLayoutChange('logoOffsetY')} className="w-full accent-cyan-400" />
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40">
                    {form.logoPreviewUrl ? (
                      <img src={form.logoPreviewUrl} alt="Logoesikatselu" className="h-full w-full object-cover" style={getLogoImageStyle(form.logoScale, form.logoOffsetX, form.logoOffsetY)} />
                    ) : (
                      <span className="text-center text-xs uppercase tracking-[0.25em] text-slate-500">Esikatselu</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button name="Peruuta" type="SECONDARY" action={closeModal} />
                <Button name={saving ? 'Tallennetaan...' : editingOrganizerId ? 'Päivitä' : 'Tallenna'} type={saving ? 'DISABLED' : 'PRIMARY'} buttonType="submit" />
              </div>
            </form>
          </div>
        </div>
      )}

      {isUserModalOpen && selectedOrganizerForUsers ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={closeUserModal}>
          <div className="w-full max-w-2xl rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Järjestäjän käyttäjät</p>
                <h2 className="mt-2 text-2xl font-black text-white">{editingOrganizerUser ? 'Muokkaa käyttäjää' : 'Lisää käyttäjä'}</h2>
                <p className="mt-2 text-sm text-slate-400">{selectedOrganizerForUsers.name}</p>
              </div>
              <button type="button" onClick={closeUserModal} className="rounded-full border border-white/10 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10">
                Sulje
              </button>
            </div>

            <form onSubmit={handleAddOrganizerUser} className="mt-6 space-y-6">
              {userModalLoading ? (
                <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-8 text-center text-slate-300">
                  Ladataan käyttäjiä...
                </div>
              ) : (
                <>
                  {editingOrganizerUser ? (
                    <div className="rounded-[28px] border border-white/10 bg-white/5 p-4">
                      <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Käyttäjä</p>
                      <p className="mt-3 text-base font-semibold text-white">{editingOrganizerUser.name}</p>
                      <p className="mt-1 text-sm text-slate-400">{editingOrganizerUser.email || 'Ei sähköpostia'}</p>
                      {editingOrganizerUser.isPending ? <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Kutsu odottaa rekisteröitymistä</p> : null}
                    </div>
                  ) : isAdmin() ? (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="organizer-user-email" className="mb-2 block text-sm font-semibold text-white">Sähköpostiosoite</label>
                        <input
                          id="organizer-user-email"
                          type="email"
                          value={userForm.email}
                          onChange={handleUserFieldChange('email')}
                          placeholder="pelaaja@example.com"
                          className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/60"
                        />
                        <p className="mt-2 text-xs text-slate-400">
                          Jos sähköpostilla löytyy jo käyttäjä, hänet lisätään suoraan. Muussa tapauksessa hänelle lähetetään rekisteröitymiskutsu kahdeksi viikoksi.
                        </p>
                      </div>

                      <div>
                        <label htmlFor="organizer-user-select" className="mb-2 block text-sm font-semibold text-white">Tai valitse käyttäjä listasta</label>
                        <select id="organizer-user-select" value={userForm.userID} onChange={handleUserFieldChange('userID')} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400/60">
                          <option value="">Valitse käyttäjä</option>
                          {assignableUsers.map((userOption) => (
                            <option key={userOption.userID} value={userOption.userID} className="bg-slate-900 text-white">
                              {userOption.name}{userOption.email ? ` (${userOption.email})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="organizer-user-email" className="mb-2 block text-sm font-semibold text-white">Sähköpostiosoite</label>
                      <input
                        id="organizer-user-email"
                        type="email"
                        value={userForm.email}
                        onChange={handleUserFieldChange('email')}
                        placeholder="pelaaja@example.com"
                        className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/60"
                      />
                      <p className="mt-2 text-xs text-slate-400">
                        Jos sähköpostilla löytyy jo käyttäjä, hänet lisätään suoraan. Muussa tapauksessa hänelle lähetetään rekisteröitymiskutsu kahdeksi viikoksi.
                      </p>
                    </div>
                  )}

                  <div className="rounded-[28px] border border-white/10 bg-white/5 p-4">
                    <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Roolit</p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {organizerRoleOptions.map((roleOption) => (
                        <FormControlLabel
                          key={roleOption.value}
                          className="m-0 rounded-2xl border border-white/10 bg-slate-950/35 px-3 py-2.5"
                          sx={{
                            alignItems: 'flex-start',
                            minHeight: '100%',
                            '.MuiCheckbox-root': {
                              marginTop: '-2px',
                            },
                            '.MuiFormControlLabel-label': {
                              display: 'block',
                              paddingTop: '4px',
                              lineHeight: 1.35,
                            },
                          }}
                          control={<Checkbox checked={userForm.roles.includes(roleOption.value)} onChange={handleRoleToggle(roleOption.value)} sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }} />}
                          label={<span className="text-sm font-semibold text-white">{roleOption.label}</span>}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3">
                <Button name="Peruuta" type="SECONDARY" action={closeUserModal} />
                <Button name={userSaving ? 'Tallennetaan...' : editingOrganizerUser ? 'Päivitä käyttäjä' : 'Lisää käyttäjä'} type={userSaving || userModalLoading ? 'DISABLED' : 'PRIMARY'} buttonType="submit" />
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {membershipConfirmState ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={closeMembershipConfirm}>
          <div className="w-full max-w-md rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-white/10 pb-4">
              <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Seuraan liittyminen</p>
              <h2 className="mt-2 text-2xl font-black text-white">Haluatko liittyä seuraan?</h2>
              <p className="mt-3 text-sm text-slate-300">Seura: <span className="font-semibold text-white">{membershipConfirmState.organizerName}</span></p>
              <p className="mt-1 text-sm text-slate-300">Jäsentaso: <span className="font-semibold text-white">{membershipConfirmState.membershipLevel}</span></p>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button name="Ei" type="SECONDARY" action={closeMembershipConfirm} />
              <Button name="Kyllä" type="PRIMARY" action={confirmMembershipLink} />
            </div>
          </div>
        </div>
      ) : null}

      {removeOrganizerUserState ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={closeRemoveOrganizerUserModal}>
          <div className="w-full max-w-md rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-white/10 pb-4">
              <p className="text-sm font-semibold uppercase tracking-[0.35em] text-rose-300">Poista käyttäjä</p>
              <h2 className="mt-2 text-2xl font-black text-white">Haluatko poistaa käyttäjän?</h2>
              <p className="mt-3 text-sm text-slate-300">
                Käyttäjä: <span className="font-semibold text-white">{removeOrganizerUserState.organizerUser?.name}</span>
              </p>
              <p className="mt-1 text-sm text-slate-300">
                Järjestäjä: <span className="font-semibold text-white">{removeOrganizerUserState.organizer?.name}</span>
              </p>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button name="Peruuta" type="SECONDARY" action={closeRemoveOrganizerUserModal} />
              <Button name={removingOrganizerUser ? 'Poistetaan...' : 'Poista'} type={removingOrganizerUser ? 'DISABLED' : 'DELETE'} action={confirmRemoveOrganizerUser} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Organizers;